import React, { useEffect, useRef } from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Tabs from 'tbk-components/src/components/Tabs';
import Tab from 'tbk-components/src/components/Tab';
import AnimatedArcs from '../../common/AnimatedArcs';
import { IAvailableIconOptions } from '../../common/IconSamples';

export interface IESGTab {
	/**
	 * ESG Tab Text
	 */
	tabText?: string;
	/**
	 * ESG Tab Content Heading
	 */
	heading?: string;
	/**
	 * ESG Tab Content Sub Heading Icon Select
	 * @control iconselect
	 */
	subHeadingIcon?: IAvailableIconOptions['calianIconSelectValues'];
	/**
	 * ESG Tab Content Sub Heading
	 * @control richtext
	 */
	subHeading?: string;
	/**
	 * ESG Tab Content Main Text
	 * @control textarea
	 */
	text?: string;
	/**
	 * ESG Tab Content CTA Button
	 */
	ctaButton?: ILink;
	/**
	 * ESG Tab Content Image Layout
	 */
	imageLayout?: 'Main Image Left' | 'Main Image Right' | 'Main Image Only';
	/**
	 * ESG Tab Content Main Image
	 */
	mainImage?: IImage;
	/**
	 * ESG Tab Content Secondary Image
	 */
	secondaryImage?: IImage;
}

export interface IESGProps extends IBaseElementProps {
	heading?: string;
	/**
	 * ESG Tab Content
	 */
	calianESGTabs?: IESGTab[];
	/**
	 * Enable Line Animations
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	inverted?: boolean;
}

/**
 * Calian ESG Component
 * @icon table-row-after
 * @block
 */
const ESG: React.FC<IESGProps> = ({
	heading,
	id,
	className,
	classNames = [],
	calianESGTabs,
	enableLineAnimations = true,
	lineAnimationDelay = 250,
	lineAnimationOffset = 150,
	inverted = false,
}) => {
	const ref = useRef(null);

	useEffect(() => {
		const tabSubheading = ref.current?.querySelectorAll(
			'.calian-esg__tab-content--sub-heading',
		);
		if (tabSubheading?.length > 0) {
			tabSubheading.forEach((subheading) => {
				if (subheading.innerHTML === '<span><br></span>') {
					subheading.remove();
				}
			});
		}
	}, [ref]);

	return (
		<div
			id={id}
			className={cn(
				className || cn('calian-esg', ...classNames),
				inverted && 'calian-esg--inverted',
				enableLineAnimations ? '' : 'disable-line-animations',
			)}
		>
			<div className="calian-esg__container container-fluid">
				<div className="row">
					<div className="col-12">
						<div className="calian-esg__tabs-container" ref={ref}>
							{heading && (
								<h2 className="h2 px-3 mb-5">{heading}</h2>
							)}
							<Tabs>
								{calianESGTabs &&
									calianESGTabs.length > 0 &&
									calianESGTabs.map((calianESGTab, index) => (
										<Tab
											title={calianESGTab.tabText}
											key={index}
										>
											<div className="calian-esg__tab-content-container container-fluid">
												<div className="row">
													<div className="calian-esg__tab-content--left col-12 col-lg-5">
														{calianESGTab.heading && (
															<h3 className="calian-esg__tab-content--heading">
																{
																	calianESGTab.heading
																}
															</h3>
														)}
														{calianESGTab.subHeading &&
															calianESGTab.subHeading !==
																null &&
															calianESGTab.subHeading !==
																'<br>' && (
																<p className="calian-esg__tab-content--sub-heading">
																	{calianESGTab.subHeadingIcon && (
																		<i
																			className={`icon icon-${kebabCase(
																				calianESGTab.subHeadingIcon,
																			)}`}
																		></i>
																	)}
																	<span
																		dangerouslySetInnerHTML={{
																			__html: calianESGTab.subHeading,
																		}}
																	/>
																</p>
															)}
														{calianESGTab.text && (
															<p className="calian-esg__tab-content--text">
																{
																	calianESGTab.text
																}
															</p>
														)}
														{calianESGTab.ctaButton
															?.label && (
															<Button
																className={cn(
																	`calian-esg__tab-content--btn btn btn-${
																		inverted
																			? 'secondary'
																			: 'primary'
																	}`,
																)}
																href={
																	calianESGTab
																		.ctaButton
																		.href
																}
																target={
																	calianESGTab
																		.ctaButton
																		.target
																}
															>
																{
																	calianESGTab
																		.ctaButton
																		.label
																}
															</Button>
														)}
													</div>
													<div className="calian-esg__tab-content--right col-12 col-lg-7">
														<div
															className={`calian-esg__tab-content--image-container ${kebabCase(
																calianESGTab.imageLayout,
															)}`}
														>
															{calianESGTab.mainImage && (
																<Image
																	{...calianESGTab.mainImage}
																	className="calian-esg__tab-content--main-image img-fluid"
																/>
															)}
															{calianESGTab.secondaryImage && (
																<Image
																	{...calianESGTab.secondaryImage}
																	className="calian-esg__tab-content--secondary-image img-fluid"
																/>
															)}
														</div>
													</div>
												</div>
											</div>
										</Tab>
									))}
							</Tabs>
						</div>
					</div>
				</div>
				{enableLineAnimations && (
					<AnimatedArcs
						arcsStyle={'Component - Two Lines'}
						arcsPosition="Right"
						arcOneColor="var(--bs-primary)"
						arcOneStrokeWidth={4}
						arcTwoColor="var(--bs-white)"
						arcTwoStrokeWidth={3}
						animationDelay={lineAnimationDelay}
						animationOffset={lineAnimationOffset}
					/>
				)}
			</div>
		</div>
	);
};

export default ESG;
